"use client";

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Image from "next/image";
import { useBuilder } from "@/context/BuilderContext";
import { BUILDER_STEPS } from "@/lib/constants";
import { cn } from "@/lib/utils";

// ─── Slug helper ─────────────────────────────────────────────────────────────

function toSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

// ─── Color palettes for placeholder gradients ────────────────────────────────

const COLOR_GRADIENTS: Record<string, [string, string]> = {
  "Jet Black": ["#0A0A0A", "#1A1A2E"],
  "Dark Brown": ["#3B2314", "#5C3D2E"],
  "Medium Brown": ["#6B4423", "#8B6B4A"],
  "Light Brown": ["#A0785A", "#C4A47A"],
  "Honey Blonde": ["#C8943E", "#E8C87A"],
  "Platinum Blonde": ["#E8D5B7", "#F5EDE0"],
  Auburn: ["#8B3A3A", "#B5564A"],
  Burgundy: ["#5C0A2A", "#8B1A4A"],
  "Balayage Brown": ["#3B2314", "#C4A47A"],
  "Balayage Blonde": ["#8B6B4A", "#F5EDE0"],
  "Silver Gray": ["#808080", "#C0C0C0"],
  "Custom Color": ["#E8A0C8", "#A0C8E8"],
};

const TEXTURE_GRADIENTS: Record<string, [string, string]> = {
  Straight: ["#8B6B4A", "#6B4423"],
  "Body Wave": ["#A0785A", "#6B4423"],
  "Loose Wave": ["#C4A47A", "#8B6B4A"],
  "Deep Curly": ["#5C3D2E", "#3B2314"],
  "Kinky Straight": ["#7A5A3A", "#5C3D2E"],
};

// ─── Step display config ─────────────────────────────────────────────────────

const STEP_LABELS: Record<string, string> = {
  base_type: "Base",
  texture: "Texture",
  length: "Length",
  color: "Color",
  cap_size: "Cap",
  extras: "Extras",
};

// ─── Preview-image steps ─────────────────────────────────────────────────────
// We only have real wig photos for these attributes (base_type, texture,
// length). Color/cap don't swap the photo — picking a real shot for every
// texture×color combination isn't possible — so the preview keeps showing
// the most meaningful chosen attribute (texture > length > base).

const PHOTO_STEPS = new Set<string>(["base_type", "texture", "length"]);
const PHOTO_PRIORITY: Array<keyof typeof STEP_LABELS> = [
  "texture",
  "length",
  "base_type",
];

// Every real-photo preview webp, by slug. Preloaded on mount so the
// top preview swaps instantly when the user picks an option (no
// per-selection network fetch). Keep in sync with the files under
// public/builder/{step}/. The ?v=3 suffix matches `singlePath`.
const PRELOAD_PREVIEW_SRCS: string[] = [
  ...["lace-top", "full-lace", "silk-top", "monofilament"].map(
    (s) => `/builder/base_type/${s}.webp?v=3`
  ),
  ...["straight", "wavy", "curly"].map((s) => `/builder/texture/${s}.webp?v=3`),
  ...["10", "14", "16", "18", "20", "22", "24", "26", "30", "32"].map(
    (s) => `/builder/length/${s}.webp?v=3`
  ),
];

// ─── Component ───────────────────────────────────────────────────────────────

export function BuilderPreview() {
  const { currentStep, selections } = useBuilder();
  const stepKey = BUILDER_STEPS[currentStep].key;
  const currentValue = selections[stepKey as keyof typeof selections];
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [prevSrc, setPrevSrc] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [failedSrcs, setFailedSrcs] = useState<Set<string>>(new Set());

  // Combo (texture+color) previews are disabled — we use per-attribute
  // wig photos, not every texture×color combination.
  const comboPath: string | null = null;
  const comboOk = false;

  // Show the photo for the attribute the user is editing (when we have one
  // for that step); otherwise the most visually meaningful prior choice.
  const { visualStep, visualValue } = useMemo(() => {
    if (PHOTO_STEPS.has(stepKey) && currentValue) {
      return { visualStep: stepKey, visualValue: currentValue };
    }
    for (const key of PHOTO_PRIORITY) {
      const val = selections[key as keyof typeof selections];
      if (val) {
        return { visualStep: key, visualValue: val };
      }
    }
    return { visualStep: null, visualValue: null };
  }, [currentValue, stepKey, selections]);

  const singlePath = visualValue && visualStep
    ? `/builder/${visualStep}/${toSlug(visualValue)}.webp?v=3`
    : null;

  // Use combo image when available AND not failed, otherwise fall back to single-step image
  const imagePath = (comboOk ? comboPath : null) || singlePath;

  const imageOk = imagePath !== null && !failedSrcs.has(imagePath);
  const imageReady = imagePath !== null && loadedSrc === imagePath;

  const handleLoad = useCallback(() => {
    if (imagePath) {
      setLoadedSrc(imagePath);
      // Keep prevSrc visible during the fade-in, then clear it after transition completes
      setTimeout(() => {
        setPrevSrc(imagePath);
        setIsTransitioning(false);
      }, 550); // slightly longer than the 500ms transition duration
    }
  }, [imagePath]);

  const handleError = useCallback(() => {
    if (imagePath) setFailedSrcs((prev) => new Set(prev).add(imagePath));
  }, [imagePath]);

  // When the target image path changes:
  // - If the new image was already loaded (same as prevSrc = browser cache hit),
  //   mark it ready immediately to prevent a flash.
  // - Otherwise, reset loadedSrc so the new image fades in properly and mark
  //   that we're transitioning (keeps prevSrc visible underneath).
  useEffect(() => {
    if (!imagePath) return;
    if (imagePath === prevSrc) {
      // This image was loaded before — mark it ready immediately (cache hit)
      setLoadedSrc(imagePath);
      setIsTransitioning(false);
    } else if (loadedSrc !== imagePath) {
      // New image — reset loaded state so it starts at opacity-0
      setLoadedSrc(null);
      setIsTransitioning(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagePath]);

  // Gradient colors for placeholder
  const gradientColors: [string, string] = (() => {
    if (selections.color && COLOR_GRADIENTS[selections.color]) {
      return COLOR_GRADIENTS[selections.color];
    }
    if (selections.texture && TEXTURE_GRADIENTS[selections.texture]) {
      return TEXTURE_GRADIENTS[selections.texture];
    }
    return ["#D4C5B5", "#E8DDD0"];
  })();

  // ─── 3D Tilt effect ──────────────────────────────────────────────────────
  const cardRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [isTilting, setIsTilting] = useState(false);
  const rafRef = useRef<number>(0);

  const MAX_TILT = 15; // degrees

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    if (!card) return;

    // Only tilt on mouse or pen; touch handled separately
    if (e.pointerType === "touch") return;

    const rect = card.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;  // 0 → 1
    const y = (e.clientY - rect.top) / rect.height;   // 0 → 1

    const tiltX = (0.5 - y) * MAX_TILT * 2;  // top = positive, bottom = negative
    const tiltY = (x - 0.5) * MAX_TILT * 2;  // left = negative, right = positive

    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      setTilt({ x: tiltX, y: tiltY });
    });
    setIsTilting(true);
  }, []);

  const handlePointerLeave = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    setTilt({ x: 0, y: 0 });
    setIsTilting(false);
  }, []);

  // Touch drag tilt
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
    setIsTilting(true);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    if (!card || !touchStart.current) return;

    const touch = e.touches[0];
    const rect = card.getBoundingClientRect();
    const x = (touch.clientX - rect.left) / rect.width;
    const y = (touch.clientY - rect.top) / rect.height;

    const tiltX = Math.max(-MAX_TILT, Math.min(MAX_TILT, (0.5 - y) * MAX_TILT * 2));
    const tiltY = Math.max(-MAX_TILT, Math.min(MAX_TILT, (x - 0.5) * MAX_TILT * 2));

    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      setTilt({ x: tiltX, y: tiltY });
    });
  }, []);

  const handleTouchEnd = useCallback(() => {
    touchStart.current = null;
    setTilt({ x: 0, y: 0 });
    setIsTilting(false);
  }, []);

  // Cleanup RAF on unmount
  useEffect(() => {
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // Warm the browser cache with every preview photo on mount so the top
  // image swaps instantly when the user picks an option (Rivky 2026-06-28
  // — "have it be super fast"). Pairs with `unoptimized` on the <Image>
  // below so the preloaded raw webp URL is the exact one the <Image>
  // requests (a cache hit, not a /_next/image round-trip).
  useEffect(() => {
    PRELOAD_PREVIEW_SRCS.forEach((src) => {
      const img = new window.Image();
      img.src = src;
    });
  }, []);

  // Build selection summary chips
  const selectionSummary = Object.entries(selections)
    .filter(([, v]) => v !== null && v !== "none" && v !== "")
    .map(([k, v]) => ({ step: STEP_LABELS[k] || k, value: v!, key: k }));

  // Preview images use today's real wig photos (base_type / texture /
  // length). Falls back to the silhouette placeholder for any attribute
  // without a photo.
  const SHOW_PREVIEW_IMAGES = true;

  return (
    <div className="w-full h-full flex items-center justify-center relative overflow-hidden p-4 sm:p-6">
      {/* Ambient background */}
      <div
        className="absolute inset-0 transition-all duration-1000"
        style={{
          background: `
            radial-gradient(ellipse 80% 60% at 50% 40%, ${gradientColors[0]}15, transparent),
            radial-gradient(ellipse 60% 80% at 30% 60%, ${gradientColors[1]}10, transparent),
            linear-gradient(to bottom, var(--color-bg), var(--color-bg-alt))
          `,
        }}
      />

      {/* Subtle decorative rings */}
      <div
        className="absolute w-[500px] h-[500px] rounded-full border opacity-[0.04] transition-all duration-1000"
        style={{ borderColor: gradientColors[0] }}
      />
      <div
        className="absolute w-[650px] h-[650px] rounded-full border opacity-[0.03] transition-all duration-1000"
        style={{ borderColor: gradientColors[1] }}
      />

      {/* Main preview card with 3D tilt */}
      <div className="relative z-10 flex h-full w-full flex-col items-center justify-center gap-3 sm:gap-4 overflow-hidden" style={{ perspective: "800px" }}>
        <div
          ref={cardRef}
          onPointerMove={handlePointerMove}
          onPointerLeave={handlePointerLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className={cn(
            "relative aspect-[3/4] h-full max-h-[calc(100%-2.5rem)] w-auto rounded-2xl overflow-hidden flex-shrink-0",
            "will-change-transform select-none"
          )}
          style={{
            transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
            transition: isTilting
              ? "transform 0.1s ease-out, box-shadow 0.1s ease-out"
              : "transform 0.5s cubic-bezier(0.23, 1, 0.32, 1), box-shadow 0.5s cubic-bezier(0.23, 1, 0.32, 1)",
            boxShadow: isTilting
              ? `${-tilt.y * 1.5}px ${tilt.x * 1.5}px 40px rgba(0,0,0,0.12), 0 8px 30px rgba(0,0,0,0.06)`
              : "0 8px 40px rgba(0,0,0,0.08)",
            transformStyle: "preserve-3d",
          }}
        >
          {/* Gradient placeholder background — always rendered behind */}
          <div
            className="absolute inset-0 transition-all duration-700"
            style={{
              background: `linear-gradient(160deg, ${gradientColors[0]}30 0%, ${gradientColors[1]}50 50%, ${gradientColors[0]}20 100%)`,
            }}
          />

          {/* Inner glow */}
          <div
            className="absolute inset-0 opacity-40"
            style={{
              background: `radial-gradient(ellipse 70% 50% at 50% 35%, ${gradientColors[0]}40, transparent)`,
            }}
          />

          {/* Previous image — stays visible until new one fully fades in (prevents blink) */}
          {SHOW_PREVIEW_IMAGES && prevSrc && isTransitioning && (
            <Image
              src={prevSrc}
              alt=""
              fill
              unoptimized
              sizes="(max-width: 640px) 240px, (max-width: 1024px) 288px, 360px"
              className="object-cover object-top opacity-100"
            />
          )}

          {/* Current image — fades in on top */}
          {SHOW_PREVIEW_IMAGES && imageOk && (
            <Image
              key={imagePath}
              src={imagePath!}
              alt={`Custom wig ${visualStep} option: ${visualValue} - Rivky Wigs Builder`}
              fill
              unoptimized
              sizes="(max-width: 640px) 240px, (max-width: 1024px) 288px, 360px"
              className={cn(
                "object-cover object-top transition-all duration-500",
                imageReady ? "opacity-100 scale-100" : "opacity-0 scale-105"
              )}
              onLoad={handleLoad}
              onError={handleError}
            />
          )}

          {/* Placeholder silhouette (shown when no image is loaded, or
              always while preview images are off) */}
          {(!SHOW_PREVIEW_IMAGES || !imageReady || !imageOk) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
              <svg
                viewBox="0 0 120 200"
                className="w-28 h-44 sm:w-36 sm:h-56 opacity-[0.12] transition-opacity duration-500"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
              >
                {/* Head oval */}
                <ellipse cx="60" cy="45" rx="26" ry="33" />
                {/* Hair flowing */}
                <path d="M34 30 Q28 55 25 80 Q22 100 26 115" strokeWidth="1.5" />
                <path d="M86 30 Q92 55 95 80 Q98 100 94 115" strokeWidth="1.5" />
                <path d="M38 22 Q30 35 28 55" strokeWidth="1.5" />
                <path d="M82 22 Q90 35 92 55" strokeWidth="1.5" />
                {/* Neck */}
                <path d="M48 76 L47 95 L73 95 L72 76" />
                {/* Shoulders */}
                <path d="M15 120 Q35 98 47 95" strokeWidth="1.2" />
                <path d="M105 120 Q85 98 73 95" strokeWidth="1.2" />
                <path d="M15 120 L10 145 L110 145 L105 120" strokeWidth="0.8" />
              </svg>

              {currentValue && (
                <div className="mt-2 px-4 py-1.5 rounded-full bg-surface/60 backdrop-blur-sm">
                  <p className="text-xs font-medium text-foreground/50 tracking-wide">
                    {currentValue}
                  </p>
                </div>
              )}

              {!currentValue && (
                <p className="text-xs text-brand-muted/40 mt-2 tracking-wide">
                  Select an option below
                </p>
              )}
            </div>
          )}

          {/* Top-start step badge */}
          <div className="absolute top-3 start-3 z-20">
            <span className="px-2.5 py-1 rounded-full bg-black/30 backdrop-blur-md text-[11px] font-medium text-white tracking-wider uppercase">
              {BUILDER_STEPS[currentStep].label}
            </span>
          </div>

          {/* Glare/shine overlay — moves with tilt */}
          {isTilting && (
            <div
              className="absolute inset-0 pointer-events-none z-30 transition-opacity duration-300"
              style={{
                background: `radial-gradient(
                  ellipse 60% 60% at ${50 + tilt.y * 2}% ${50 - tilt.x * 2}%,
                  rgba(255,255,255,0.15) 0%,
                  transparent 70%
                )`,
              }}
            />
          )}

          {/* Bottom gradient fade */}
          <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/10 to-transparent pointer-events-none" />
        </div>

        {/* Selection chips */}
        {selectionSummary.length > 0 && (
          <div className="flex flex-wrap justify-center gap-1.5 max-w-xs sm:max-w-sm">
            {selectionSummary.map(({ step, value, key }) => (
              <span
                key={key}
                className={cn(
                  "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] sm:text-xs font-medium transition-all duration-300",
                  key === stepKey
                    ? "bg-brand-gold/15 text-brand-gold border border-brand-gold/25 shadow-sm"
                    : "bg-surface/70 text-brand-muted/80 border border-theme-border/60"
                )}
              >
                {/* Color dot for color selections */}
                {key === "color" && COLOR_GRADIENTS[value] && (
                  <span
                    className="w-2.5 h-2.5 rounded-full border border-white/50 flex-shrink-0"
                    style={{
                      background: `linear-gradient(135deg, ${COLOR_GRADIENTS[value][0]}, ${COLOR_GRADIENTS[value][1]})`,
                    }}
                  />
                )}
                <span className="opacity-50">{step}</span>
                <span className="font-semibold">{value}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
